import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Baby, Youtube, Send, CheckCircle2, Circle, RefreshCcw, MessageSquare, Video, User, Mic, StopCircle, PlayCircle, Trash } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * KidCreatorsAdminTab — Admin / Parent-in-Control dashboard for overseeing
 * Tap IN Kids Network creators (like @zizisworld).
 *
 * Admin can, on behalf of the kid creator:
 *   · Import a YouTube episode as their next video
 *   · Post a story to their timeline
 *   · Toggle publish/unpublish on any of their videos
 *   · Edit name / handle / bio / picture
 *
 * Every action is audit-logged server-side with the admin's user_id.
 */
export default function KidCreatorsAdminTab() {
  const [creators, setCreators] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/kid-creators");
      setCreators(r.data || []);
      if (r.data?.[0] && !active) setActive(r.data[0]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-kid-creators">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 font-semibold flex items-center gap-2">
            <Baby className="w-4 h-4" /> Parent in Control
          </div>
          <h2 className="font-display font-black text-3xl text-white mt-1">Kid Creators</h2>
          <div className="text-white/60 text-sm mt-1">
            Post on behalf of kid creators you supervise. Every action is logged with your admin ID for compliance.
          </div>
        </div>
        <button data-testid="kid-creators-refresh" onClick={load} className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-4 py-2 rounded-full inline-flex items-center gap-1.5">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading && <div className="text-white/60">Loading kid creators…</div>}
      {!loading && creators.length === 0 && (
        <div className="tv-glass rounded-2xl p-8 text-center text-white/70">
          No kid creators yet. Mark a creator account with <code className="bg-white/10 px-1.5 py-0.5 rounded">is_kids_safe=true</code> to surface them here.
        </div>
      )}

      {creators.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar list */}
          <aside className="space-y-2">
            {creators.map((c) => {
              const isActive = active?.user_id === c.user_id;
              return (
                <button
                  key={c.user_id}
                  data-testid={`kid-creator-${c.user_id}`}
                  onClick={() => setActive(c)}
                  className={`w-full text-left rounded-xl p-3 flex items-center gap-3 transition ${
                    isActive ? "bg-tv-accent text-black" : "tv-glass text-white hover:bg-white/15"
                  }`}
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/30 shrink-0 bg-gradient-to-br from-fuchsia-500 to-sky-500 flex items-center justify-center text-lg font-black">
                    {c.picture ? <img src={c.picture} alt="" className="w-full h-full object-cover" /> : (c.name || "?")[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black truncate">{c.name}</div>
                    <div className={`text-[11px] truncate ${isActive ? "text-black/60" : "text-white/55"}`}>
                      {c.handle ? `@${c.handle}` : c.email} · {c.video_count ?? 0} vids · {c.story_count ?? 0} posts
                    </div>
                  </div>
                </button>
              );
            })}
          </aside>

          <main>{active && <KidCreatorPanel creator={active} onChange={load} />}</main>
        </div>
      )}
    </div>
  );
}

function KidCreatorPanel({ creator, onChange }) {
  const [tab, setTab] = useState("post");
  return (
    <div className="tv-glass rounded-2xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start gap-4 pb-4 border-b border-white/10">
        <div className="w-20 h-20 rounded-2xl overflow-hidden ring-4 ring-white/20 bg-gradient-to-br from-fuchsia-500 to-sky-500 shrink-0">
          {creator.picture ? <img src={creator.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-10 h-10 text-white m-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-2xl text-white">{creator.name}</div>
          {creator.handle && <div className="text-fuchsia-300 text-sm font-bold">@{creator.handle}</div>}
          <div className="text-white/60 text-xs mt-1 line-clamp-2">{creator.bio || "No bio yet."}</div>
          <div className="flex gap-3 mt-2 text-[11px] text-white/60">
            <span><b className="text-white">{creator.video_count ?? 0}</b> videos</span>
            <span><b className="text-white">{creator.story_count ?? 0}</b> posts</span>
            <span><b className="text-amber-300">{creator.posted_by_admin_count ?? 0}</b> posted by admin</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { k: "post", label: "Post Story", icon: MessageSquare },
          { k: "youtube", label: "Import YouTube", icon: Youtube },
          { k: "voice", label: "Learn Voice", icon: Mic },
          { k: "profile", label: "Edit Profile", icon: User },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              data-testid={`kid-creator-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition ${
                tab === t.k ? "bg-white text-black" : "bg-white/10 text-white/80 hover:bg-white/20"
              }`}
            ><Icon className="w-4 h-4" /> {t.label}</button>
          );
        })}
      </div>

      {tab === "post" && <PostStoryForm creator={creator} onChange={onChange} />}
      {tab === "youtube" && <ImportYouTubeForm creator={creator} onChange={onChange} />}
      {tab === "voice" && <VoiceOnBehalfForm creator={creator} />}
      {tab === "profile" && <EditProfileForm creator={creator} onChange={onChange} />}
    </div>
  );
}

function PostStoryForm({ creator, onChange }) {
  const [text, setText] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim() && !mediaUrl.trim()) { toast.error("Write something or attach media first"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/kid-creators/${creator.user_id}/story`, {
        text: text.trim(),
        media_url: mediaUrl.trim() || null,
        kind: mediaUrl.trim() ? (mediaUrl.match(/\.(mp4|mov|webm)$/i) ? "video" : "image") : "text",
      });
      toast.success(`Story posted on behalf of @${creator.handle || creator.name}`);
      setText(""); setMediaUrl("");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="text-sm text-white/70">
        You're posting <b className="text-white">as</b> <span className="text-fuchsia-300 font-bold">@{creator.handle || creator.name}</span>. The post will carry your admin ID for audit.
      </div>
      <textarea
        data-testid="kid-creator-story-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Hey fam! 🌟 What's Zi Zi up to today?"
        rows={3}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400"
      />
      <input
        data-testid="kid-creator-story-media"
        value={mediaUrl}
        onChange={(e) => setMediaUrl(e.target.value)}
        placeholder="Image or video URL (optional)"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400"
      />
      <div className="flex justify-end">
        <button
          data-testid="kid-creator-story-submit"
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-400 hover:to-pink-400 text-white font-black px-5 py-2.5 rounded-full disabled:opacity-60"
        ><Send className="w-4 h-4" /> {busy ? "Posting…" : "Post story"}</button>
      </div>
    </div>
  );
}

function ImportYouTubeForm({ creator, onChange }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!url.trim()) { toast.error("Paste a YouTube URL"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/kid-creators/${creator.user_id}/youtube-import`, {
        url: url.trim(), title: title.trim() || undefined, description: desc.trim() || undefined, permission_granted: true,
      });
      toast.success(`Episode imported for @${creator.handle || creator.name}`);
      setUrl(""); setTitle(""); setDesc("");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="text-sm text-white/70">
        Paste any YouTube URL — it'll be embedded on <span className="text-fuchsia-300 font-bold">@{creator.handle || creator.name}</span>'s Tap IN Kids channel. Duplicate imports are skipped automatically.
      </div>
      <input
        data-testid="kid-creator-yt-url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://youtu.be/…"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400"
      />
      <div className="grid md:grid-cols-2 gap-3">
        <input
          data-testid="kid-creator-yt-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Episode title (optional)"
          className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400"
        />
        <input
          data-testid="kid-creator-yt-desc"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Short description (optional)"
          className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400"
        />
      </div>
      <div className="flex justify-end">
        <button
          data-testid="kid-creator-yt-submit"
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-black px-5 py-2.5 rounded-full disabled:opacity-60"
        ><Youtube className="w-4 h-4" /> {busy ? "Importing…" : "Import episode"}</button>
      </div>
    </div>
  );
}

function EditProfileForm({ creator, onChange }) {
  const [form, setForm] = useState({
    name: creator.name || "",
    handle: creator.handle || "",
    bio: creator.bio || "",
    picture: creator.picture || "",
    is_kids_safe: !!creator.is_kids_safe,
    is_creator_approved: !!creator.is_creator_approved,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/kid-creators/${creator.user_id}/profile`, form);
      toast.success("Profile updated");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        <input data-testid="kid-creator-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Display name" className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white" />
        <input data-testid="kid-creator-handle" value={form.handle} onChange={(e) => set("handle", e.target.value.replace(/^@/, "").toLowerCase())} placeholder="handle (no @)" className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white" />
      </div>
      <textarea data-testid="kid-creator-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Short bio" rows={2} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white" />
      <input data-testid="kid-creator-picture" value={form.picture} onChange={(e) => set("picture", e.target.value)} placeholder="Avatar / picture URL" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white" />
      <div className="flex flex-wrap gap-5 text-sm text-white/80">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={form.is_kids_safe} onChange={(e) => set("is_kids_safe", e.target.checked)} /> Kids-safe brand
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={form.is_creator_approved} onChange={(e) => set("is_creator_approved", e.target.checked)} /> Creator approved
        </label>
      </div>
      <div className="flex justify-end">
        <button data-testid="kid-creator-profile-save" onClick={submit} disabled={busy}
          className="inline-flex items-center gap-2 bg-white hover:bg-white/90 text-black font-black px-5 py-2.5 rounded-full disabled:opacity-60"
        ><CheckCircle2 className="w-4 h-4" /> {busy ? "Saving…" : "Save profile"}</button>
      </div>
    </div>
  );
}

/**
 * VoiceOnBehalfForm — admin records (or uploads) a Learn & Play voice
 * pack on behalf of the parent-in-control for this kid creator. Useful
 * when the admin is also the supervising parent (like ZiZi ↔ admin).
 * Writes to `/admin/kids/voice-pack/{parent_user_id}` so the action is
 * audit-logged with the admin's user_id.
 */
function VoiceOnBehalfForm({ creator }) {
  const parentId = creator.controlled_by_parent_id || creator.user_id;
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recorder, setRecorder] = useState(null);
  const [label, setLabel] = useState(`Voice for @${creator.handle || creator.name}`);
  const [lastUrl, setLastUrl] = useState(null);
  const chunksRef = React.useRef([]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size > 2_000_000) { toast.error("Recording too long — keep it under ~20s"); return; }
        const reader = new FileReader();
        reader.onloadend = async () => {
          setSaving(true);
          try {
            await api.post(`/admin/kids/voice-pack/${parentId}`, { data_url: reader.result, label });
            setLastUrl(reader.result);
            toast.success(`Voice uploaded for @${creator.handle || creator.name}`);
          } catch (err) { toast.error(formatApiError(err)); }
          finally { setSaving(false); }
        };
        reader.readAsDataURL(blob);
      };
      rec.start();
      setRecorder(rec);
      setRecording(true);
      setTimeout(() => { try { if (rec.state !== "inactive") rec.stop(); } catch {} setRecording(false); }, 20_000);
    } catch { toast.error("Microphone permission denied"); }
  };
  const stopRec = () => { try { recorder?.stop(); } catch {} setRecording(false); };

  return (
    <div className="space-y-3">
      <div className="text-sm text-white/70">
        You're managing the Learn &amp; Play voice for <span className="text-fuchsia-300 font-bold">@{creator.handle || creator.name}</span>. The clip (≤&nbsp;20s) plays as the welcome greeting when your kid opens flashcards.
      </div>
      <input
        data-testid="kid-voice-label"
        value={label}
        onChange={(e) => setLabel(e.target.value.slice(0, 80))}
        placeholder="Label"
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white"
      />
      <div className="flex flex-wrap gap-2">
        {!recording ? (
          <button data-testid="kid-voice-record" onClick={startRec} disabled={saving}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-black px-5 py-2.5 rounded-full disabled:opacity-50"
          ><Mic className="w-4 h-4" /> {saving ? "Uploading…" : "Record (up to 20s)"}</button>
        ) : (
          <button data-testid="kid-voice-stop" onClick={stopRec}
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-red-600 font-black px-5 py-2.5 rounded-full animate-pulse"
          ><StopCircle className="w-4 h-4" /> Stop &amp; upload</button>
        )}
        {lastUrl && (
          <button data-testid="kid-voice-preview" onClick={() => { try { new Audio(lastUrl).play(); } catch {} }}
            className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-black px-5 py-2.5 rounded-full"
          ><PlayCircle className="w-4 h-4" /> Preview</button>
        )}
      </div>
      {recording && <div className="text-red-300 text-sm font-bold flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 animate-ping" /> Recording…</div>}
      <div className="text-white/55 text-xs">Tip: say "Hi! Let's learn together on Tap IN Kids!" — make it warm and slow so your kid hears every word. Audit-logged with your admin ID.</div>
    </div>
  );
}

